import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';

type Grant = {
  id: string; label: string; scopes: string[]; has_pin: boolean; expires_at: string | null;
  status: string; created_at: string; last_access_at: string | null; access_count: number;
};
type LogRow = { ip: string | null; scopes: string; at_utc: string; ledger_seq: number };

const SCOPES = [
  { key: 'devices', label: 'Devices' },
  { key: 'notifications', label: 'Notifications to police' },
  { key: 'reconciliation', label: 'Police-list reconciliation' },
  { key: 'accounts', label: 'Bank accounts & cards (masked)' },
  { key: 'financial-full', label: 'Bank accounts & cards — FULL numbers (requires PIN)' },
] as const;

const linkFor = (id: string) => `${window.location.origin}/i/${id}`;
const fmt = (t: string) => t.replace('T', ' ').replace(/\.\d+Z$/, ' UTC');

export function Grants() {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [logFor, setLogFor] = useState<string | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  async function load() {
    try { setGrants((await fetch('/api/grants').then((r) => r.json())).grants ?? []); } catch { /* offline */ }
  }
  useEffect(() => { load(); }, []);

  async function revoke(id: string, label: string) {
    if (!confirm(`Revoke access for "${label}"? The link stops working immediately.`)) return;
    await fetch(`/api/grants/${id}/revoke`, { method: 'POST' });
    await load();
  }
  async function openLog(id: string) {
    if (logFor === id) { setLogFor(null); return; }
    setLogFor(id);
    setLog((await fetch(`/api/grants/${id}/log`).then((r) => r.json())).log ?? []);
  }
  function copy(id: string) { navigator.clipboard?.writeText(linkFor(id)); setCopied(id); setTimeout(() => setCopied(null), 1500); }

  return (
    <Page
      eyebrow="Evidence & access"
      title="Inspector grants"
      actions={<button className="btn btn-primary" type="button" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Close' : '+ New grant'}</button>}
    >
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Create a read-only link for a PPU officer or solicitor, scoped to exactly the categories you
          choose. Add a PIN for a second factor and an expiry date. Links are revocable instantly, and
          <b> every view is logged</b> to the tamper-evident ledger. The credential vault is never shareable.
        </p>
      </section>

      {showAdd && <AddForm onDone={() => { setShowAdd(false); load(); }} />}

      <div className="card">
        <div className="panel-head"><h3>Grants</h3><span className="hint">{grants.length}</span></div>
        <div className="ledger">
          {grants.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No grants yet.</div>}
          {grants.map((g) => (
            <div key={g.id} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className={`badge badge-${g.status === 'active' ? 'ok' : 'bad'}`}>{g.status.toUpperCase()}</span>
                <strong>{g.label}</strong>
                {g.has_pin && <span className="badge badge-mute">PIN</span>}
                {g.expires_at && <span className="badge badge-warn">expires {g.expires_at.slice(0, 10)}</span>}
                <span className="muted" style={{ fontSize: 12 }}>{g.scopes.join(', ')}</span>
              </div>
              {g.status === 'active' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0', flexWrap: 'wrap' }}>
                  <input readOnly value={linkFor(g.id)} className="mono"
                    style={{ flex: 1, minWidth: 240, fontSize: 11.5, padding: '6px 8px', border: '1px solid var(--border2)', borderRadius: 6, background: 'var(--surface2)' }} />
                  <button className="btn btn-ghost" type="button" onClick={() => copy(g.id)}>{copied === g.id ? 'Copied' : 'Copy link'}</button>
                </div>
              )}
              <div className="ledger-time" style={{ display: 'flex', gap: 12 }}>
                <span>created {fmt(g.created_at)}</span>
                <span>{g.access_count} view{g.access_count === 1 ? '' : 's'}{g.last_access_at ? ` · last ${fmt(g.last_access_at)}` : ''}</span>
                <button className="btn-link" style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 12 }} onClick={() => openLog(g.id)}>access log</button>
                {g.status === 'active' && <button style={{ background: 'none', border: 'none', color: 'var(--bad)', cursor: 'pointer', padding: 0, fontSize: 12 }} onClick={() => revoke(g.id, g.label)}>revoke</button>}
              </div>
              {logFor === g.id && (
                <div style={{ marginTop: 8, padding: 8, background: 'var(--surface2)', borderRadius: 6, fontSize: 12 }}>
                  {log.length === 0 && <div className="muted">No views yet.</div>}
                  {log.map((l, i) => (
                    <div key={i} className="mono muted" style={{ fontSize: 11.5 }}>{fmt(l.at_utc)} · {l.ip ?? '—'} · {JSON.parse(l.scopes).join(',')} · ledger seq {l.ledger_seq}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="panel-foot mono">every inspector view = one logged disclosure in the ledger</div>
      </div>
    </Page>
  );
}

function AddForm({ onDone }: { onDone: () => void }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>(['devices', 'notifications', 'reconciliation']);
  const [usePin, setUsePin] = useState(false);
  const [pin, setPin] = useState('');
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(s: string) {
    setScopes((cur) => {
      const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
      return next;
    });
    if (s === 'financial-full') {
      if (!scopes.includes('financial-full')) {
        setUsePin(true);
      } else {
        setPin('');
      }
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!label.trim()) { setErr('Give the grant a name (e.g. the officer or firm).'); return; }
    if (scopes.length === 0) { setErr('Select at least one category.'); return; }
    if (scopes.includes('financial-full') && pin.trim().length < 8) { setErr('An access code of at least 8 characters is required for full financial access.'); return; }
    if (usePin && !scopes.includes('financial-full') && pin.trim().length < 4) { setErr('PIN should be at least 4 digits.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/grants', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ label, scopes, ...(usePin ? { pin: pin.trim() } : {}), ...(useExpiry && expiry ? { expires_at: expiry } : {}) }),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed'); return; }
      onDone();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }

  return (
    <form className="card card-pad" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15 }}>New inspector grant</h3>
      <label className="fld"><span>Who is this for?</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. PPU Officer 4471 / Smith & Co Solicitors" />
      </label>
      <div className="fld"><span>Categories they may view</span>
        <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
          {SCOPES.map((s) => (
            <div key={s.key}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
                <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} />{s.label}
              </label>
              {s.key === 'financial-full' && scopes.includes('financial-full') && (
                <div style={{ marginLeft: 24, marginTop: 4, padding: '6px 10px', background: 'var(--warn-bg, #fff8e1)', border: '1px solid var(--warn, #f59e0b)', borderRadius: 6, fontSize: 12, color: 'var(--warn-text, #92400e)' }}>
                  Warning: this exposes full account and card numbers to whoever holds the link + access code.
                  It is a point-in-time snapshot. Only share with a trusted officer or solicitor.
                  An access code (min 8 characters) is mandatory for this scope.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
        <div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
            <input type="checkbox" checked={usePin} onChange={(e) => setUsePin(e.target.checked)} /> Require a PIN
          </label>
          {usePin && <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" inputMode="numeric"
            style={{ marginTop: 6, padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 6, width: 140 }} />}
        </div>
        <div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
            <input type="checkbox" checked={useExpiry} onChange={(e) => setUseExpiry(e.target.checked)} /> Set an expiry date
          </label>
          {useExpiry && <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
            style={{ marginTop: 6, padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 6 }} />}
        </div>
      </div>
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '8px 0' }}>{err}</div>}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 10 }}>{busy ? 'Creating…' : 'Create grant'}</button>
    </form>
  );
}
